/**
 * Result Detail Page
 * View detailed results for a specific attempt
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { attemptsApi } from '../api';
import { AttemptResults, ScoredResponse } from '../../types/models';

export const ResultDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [results, setResults] = useState<AttemptResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadResults();
    }
  }, [id]);

  const loadResults = async () => {
    try {
      setLoading(true);
      // Get attempt details
      const attemptData = await attemptsApi.getById(id!);
      
      // Calculate category scores if we have questions and responses
      let categoryScores: any[] = [];
      if (attemptData.questions && attemptData.responses) {
        const categoryScoresMap = new Map<string, { earned: number; possible: number }>();
        
        for (const response of attemptData.responses) {
          const question = attemptData.questions.find((q: any) => q.id === response.question_id);
          if (question?.category) {
            const category = question.category;
            const current = categoryScoresMap.get(category) || { earned: 0, possible: 0 };
            current.earned += Number(response.points_earned || 0);
            current.possible += Number(question.points_possible || 0);
            categoryScoresMap.set(category, current);
          }
        }

        categoryScores = Array.from(categoryScoresMap.entries())
          .map(([category, scores]) => ({
            category,
            score_earned: scores.earned,
            score_possible: scores.possible,
            score_percent: scores.possible > 0 ? (scores.earned / scores.possible) * 100 : 0,
          }))
          .sort((a, b) => a.score_percent - b.score_percent);
      }

      // If attempt is completed, we can get full results
      // Otherwise, show what we have
      if (attemptData.status === 'completed') {
        // For completed attempts, we can reconstruct results from the attempt data
        const resultsData: AttemptResults = {
          attempt: attemptData as any,
          score_raw: attemptData.score_raw || 0,
          score_max: attemptData.score_max || 0,
          score_percent: attemptData.score_percent || 0,
          passed: attemptData.passed,
          time_spent_seconds: attemptData.time_spent_seconds,
          results_by_question: attemptData.responses?.map((response: any) => {
            const question = attemptData.questions?.find((q: any) => q.id === response.question_id);
            return {
              ...response,
              question: question,
            };
          }) || [],
          category_scores: categoryScores,
        };
        setResults(resultsData);
      } else {
        // For in-progress attempts, show current state
        setResults({
          attempt: attemptData as any,
          score_raw: attemptData.score_raw || 0,
          score_max: attemptData.score_max || 0,
          score_percent: attemptData.score_percent || 0,
          passed: attemptData.passed,
          time_spent_seconds: attemptData.time_spent_seconds,
          results_by_question: attemptData.responses?.map((response: any) => {
            const question = attemptData.questions?.find((q: any) => q.id === response.question_id);
            return {
              ...response,
              question: question,
            };
          }) || [],
          category_scores: categoryScores,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatStatus = (status: string) => {
    if (status === 'in_progress') return 'In Progress';
    if (status === 'completed') return 'Completed';
    if (status === 'abandoned') return 'Abandoned';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return <div className="loading">Loading results...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!results) {
    return <div className="error">Results not found</div>;
  }

  const { attempt, score_raw, score_max, score_percent, passed, time_spent_seconds, results_by_question, category_scores } = results;
  
  // For admin view, always show all information regardless of result_settings
  const categoryScores = category_scores || [];
  const bestCategories = [...categoryScores]
    .sort((a, b) => b.score_percent - a.score_percent)
    .slice(0, 3);
  const worstCategories = [...categoryScores]
    .sort((a, b) => a.score_percent - b.score_percent)
    .slice(0, 3);

  return (
    <div className="result-detail">
      <div className="result-header">
        <h1>Assessment Results</h1>
        <div className="result-actions">
          <Link to="/results" className="btn btn-secondary">
            Back to Results List
          </Link>
        </div>
      </div>

      <div className="result-summary">
        <div className="summary-card">
          <h2>Score Summary</h2>
          <div className="score-display">
            <div className="score-main">
              {(score_raw ?? 0).toFixed(1)} / {(score_max ?? 0).toFixed(1)}
            </div>
            <div className="score-percent">{(score_percent ?? 0).toFixed(1)}%</div>
            {passed !== null && (
              <div className={`score-status ${passed ? 'passed' : 'failed'}`}>
                {passed ? '✓ Passed' : '✗ Failed'}
              </div>
            )}
          </div>
        </div>

        <div className="summary-card">
          <h2>Attempt Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Status:</span>
              <span className={`status-badge status-${attempt.status}`}>
                      {formatStatus(attempt.status)}
                    </span>
            </div>
            <div className="info-item">
              <span className="label">Time Spent:</span>
              <span className="value">{formatTime(time_spent_seconds)}</span>
            </div>
            <div className="info-item">
              <span className="label">Started:</span>
              <span className="value">
                {new Date(attempt.started_at).toLocaleString()}
              </span>
            </div>
            {attempt.completed_at && (
              <div className="info-item">
                <span className="label">Completed:</span>
                <span className="value">
                  {new Date(attempt.completed_at).toLocaleString()}
                </span>
              </div>
            )}
            <div className="info-item">
              <span className="label">User ID:</span>
              <span className="value">{attempt.user_id}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Performance - Always show for admin view */}
      {categoryScores.length > 0 && (
        <div className="category-performance-section">
          {bestCategories.length > 0 && (
            <div className="best-categories">
              <h3>Strongest Areas</h3>
              <div className="category-list">
                {bestCategories.map((cat) => (
                  <div key={cat.category} className="category-item">
                    <span className="category-name">{cat.category}</span>
                    <span className="category-score">
                      {(cat.score_earned ?? 0).toFixed(1)} / {(cat.score_possible ?? 0).toFixed(1)} ({(cat.score_percent ?? 0).toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {worstCategories.length > 0 && (
            <div className="worst-categories">
              <h3>Areas for Improvement</h3>
              <div className="category-list">
                {worstCategories.map((cat) => (
                  <div key={cat.category} className="category-item">
                    <span className="category-name">{cat.category}</span>
                    <span className="category-score">
                      {(cat.score_earned ?? 0).toFixed(1)} / {(cat.score_possible ?? 0).toFixed(1)} ({(cat.score_percent ?? 0).toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {categoryScores.length > 0 && (
            <div className="all-categories">
              <h3>All Category Results</h3>
              <div className="category-list">
                {categoryScores
                  .sort((a, b) => b.score_percent - a.score_percent) // Sort by score descending
                  .map((cat) => (
                    <div key={cat.category} className="category-item">
                      <span className="category-name">{cat.category}</span>
                      <span className="category-score">
                        {(cat.score_earned ?? 0).toFixed(1)} / {(cat.score_possible ?? 0).toFixed(1)} ({(cat.score_percent ?? 0).toFixed(1)}%)
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="questions-review">
        <h2>Question Review</h2>
        <div className="questions-list">
          {results_by_question.length === 0 ? (
            <p>No responses recorded.</p>
          ) : (
            results_by_question.map((scoredResponse: ScoredResponse, index: number) => {
              const question = scoredResponse.question;
              if (!question) return null;

              // Check if this is a rating scale question (has partial credit options strictly between 0 and 1)
              const isRatingScale = question.answer_options.some(
                (opt) => opt.partial_credit_weight > 0 && opt.partial_credit_weight < 1
              );

              let resultClass: string;
              if (isRatingScale) {
                resultClass = 'neutral';
              } else {
                resultClass = scoredResponse.is_correct ? 'correct' : 'incorrect';
              }

              return (
                <div
                  key={scoredResponse.id}
                  className={`question-result ${resultClass}`}
                >
                  <div className="question-header">
                    <span className="question-number">Question {index + 1}</span>
                    <span className="question-points">
                      {(scoredResponse.points_earned ?? 0).toFixed(1)} / {question.points_possible} points
                    </span>
                    {!isRatingScale && (
                      <span
                        className={`result-badge ${
                          scoredResponse.is_correct ? 'correct' : 'incorrect'
                        }`}
                      >
                        {scoredResponse.is_correct ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    )}
                  </div>
                  <div className="question-text">
                    <strong>{question.prompt_text}</strong>
                  </div>
                  <div className="response-details">
                    {question.question_type === 'short_answer' ? (
                      <div>
                        <strong>Your answer:</strong> {scoredResponse.text_response || 'No response'}
                      </div>
                    ) : (
                      <div>
                        <strong>Selected:</strong>{' '}
                        {scoredResponse.selected_option_ids.length > 0
                          ? question.answer_options
                              .filter((opt) =>
                                scoredResponse.selected_option_ids.includes(opt.id)
                              )
                              .map((opt) => opt.option_text)
                              .join(', ')
                          : 'No selection'}
                      </div>
                    )}
                    {!isRatingScale && question.answer_options.some((opt) => opt.is_correct) && (
                      <div className="correct-answer">
                        <strong>Correct answer:</strong>{' '}
                        {question.answer_options
                          .filter((opt) => opt.is_correct)
                          .map((opt) => opt.option_text)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                  {question.explanation && (
                    <div className="explanation">{question.explanation}</div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
